<!--
  AgenteList — orders of one agent (redesigned 2026-06-04, columns
  restored 2026-06-08).

  Column set mirrors the legacy Kendo grid (10 columns):
    Agente | N° Ordine | Preventivo | Riferimento | Ragione sociale |
    Data ordine | Data evasione | Stato | DDT | Azioni

  Row click opens the order-details modal directly (matches the legacy
  behaviour where users tap on a row to see the details). The
  expand-row inline summary is gone — everything lives in the modal.

  Two layout modes:
    - `embedded` (drawer inside /accesso-funzionari): no own header,
      compact spacing.
    - standalone (route /agentsorders?token=…): full page with sticky
      header.

  Desktop-only. The two child widgets (tracking + order-details) are
  unchanged — their dedicated restyles are scheduled separately.
-->

<div class="orders-page" [class.orders-page--embedded]="embedded">

  <!-- ─── Sticky header (only standalone mode) ─────────────────── -->
  @if (!embedded) {
    <header class="orders-header">
      <div class="orders-header__titles">
        <h1 class="orders-header__title">📦 Ordini agente</h1>
        <p class="orders-header__subtitle">
          @if (hasOrders) {
            {{ filteredOrders.length }} di {{ orders.length }} ordini
            @if (partialCount > 0) {
              · <span class="orders-header__partial">{{ partialCount }} parziali</span>
            }
          } @else if (loading) {
            Caricamento…
          } @else {
            Nessun ordine trovato per questo agente
          }
        </p>
      </div>

      <div class="orders-header__controls">
        <div class="search-wrap">
          <input
            type="text"
            class="search-input"
            placeholder="Cerca per n° ordine, cliente, indirizzo…"
            [(ngModel)]="searchText"
            (ngModelChange)="onSearchInput()"
            aria-label="Cerca ordine"
          />
          @if (searchText) {
            <button
              type="button"
              class="search-clear"
              (click)="clearSearch()"
              aria-label="Cancella ricerca"
              title="Cancella ricerca"
            >×</button>
          }
        </div>
      </div>
    </header>
  } @else {
    <!-- Embedded mode: only the filter bar, no titles -->
    <div class="orders-filter-bar">
      <div class="search-wrap">
        <input
          type="text"
          class="search-input"
          placeholder="Cerca ordine…"
          [(ngModel)]="searchText"
          (ngModelChange)="onSearchInput()"
          aria-label="Cerca ordine"
        />
        @if (searchText) {
          <button
            type="button"
            class="search-clear"
            (click)="clearSearch()"
            aria-label="Cancella ricerca"
          >×</button>
        }
      </div>
      <div class="orders-filter-bar__stats">
        {{ filteredOrders.length }}{{ filteredOrders.length !== orders.length ? ' / ' + orders.length : '' }} ordini
        @if (partialCount > 0) {
          · <span class="orders-filter-bar__partial">{{ partialCount }} parziali</span>
        }
      </div>
    </div>
  }

  <!-- ─── Table card ────────────────────────────────────────────── -->
  <section class="orders-table-card" aria-label="Elenco ordini">

    @if (loading && !hasOrders) {
      <div class="table-skeleton" aria-busy="true">
        @for (i of [1, 2, 3, 4, 5, 6, 7, 8]; track i) {
          <div class="table-skeleton__row">
            <div class="table-skeleton__line table-skeleton__line--lg"></div>
            <div class="table-skeleton__line table-skeleton__line--md"></div>
            <div class="table-skeleton__line table-skeleton__line--sm"></div>
          </div>
        }
      </div>
    } @else if (filteredOrders.length === 0) {
      <div class="orders-empty">
        <div class="orders-empty__icon" aria-hidden="true">
          @if (!hasOrders) { 📭 } @else { 🔎 }
        </div>
        <p class="orders-empty__msg">
          @if (!hasOrders) {
            Nessun ordine associato a questo agente.
          } @else if (searchText) {
            Nessun ordine corrisponde a "<strong>{{ searchText }}</strong>".
          } @else {
            Nessun risultato.
          }
        </p>
      </div>
    } @else {
      <div class="orders-table-scroll">
        <table class="orders-table" role="table">
          <thead>
            <tr>
              <th class="col-agent">Agente</th>
              <th class="col-order sortable" (click)="sort('or_order_number')">
                N° Ordine <span class="sort-icon">{{ sortIcon('or_order_number') }}</span>
              </th>
              <th class="col-quote sortable" (click)="sort('or_quote_number')">
                Preventivo <span class="sort-icon">{{ sortIcon('or_quote_number') }}</span>
              </th>
              <th class="col-ref sortable" (click)="sort('or_note')">
                Riferimento <span class="sort-icon">{{ sortIcon('or_note') }}</span>
              </th>
              <th class="col-customer sortable" (click)="sort('or_customer')">
                Ragione sociale <span class="sort-icon">{{ sortIcon('or_customer') }}</span>
              </th>
              <th class="col-date sortable" (click)="sort('or_order_date')">
                Data ordine <span class="sort-icon">{{ sortIcon('or_order_date') }}</span>
              </th>
              <th class="col-closing sortable" (click)="sort('or_closing_date')">
                Data evasione <span class="sort-icon">{{ sortIcon('or_closing_date') }}</span>
              </th>
              <th class="col-status">Stato</th>
              <th class="col-ddt">DDT</th>
              <th class="col-actions" aria-label="Azioni">Azioni</th>
            </tr>
          </thead>
          <tbody>
            @for (order of pagedOrders; track order.order_id) {
              <tr
                class="orders-row"
                [class.orders-row--partial]="isPartial(order)"
                [attr.data-priority]="priorityVariant(order)"
                (click)="openOrderDetails(order)"
                title="Apri dettagli ordine"
              >
                <td class="col-agent">
                  @if (order.ag_first_name || order.ag_last_name) {
                    <span class="cell-agent">👤 {{ order.ag_first_name }} {{ order.ag_last_name }}</span>
                  } @else {
                    <span class="cell-muted">—</span>
                  }
                </td>

                <td class="col-order">
                  <span class="cell-order-num">{{ order.or_order_number || '—' }}</span>
                  @if (isPartial(order)) {
                    <span class="cell-partial-pill" title="Ordine parziale">P</span>
                  }
                </td>

                <td class="col-quote">
                  @if (order.or_quote_number) {
                    <span class="cell-quote-val">{{ order.or_quote_number }}</span>
                  } @else {
                    <span class="cell-muted">—</span>
                  }
                </td>

                <td class="col-ref">
                  @if (order.or_note) {
                    <span class="cell-ref" [title]="order.or_note">{{ order.or_note }}</span>
                  } @else {
                    <span class="cell-muted">—</span>
                  }
                </td>

                <td class="col-customer">
                  <span class="cell-customer">{{ order.or_customer || '—' }}</span>
                  @if (order.or_address) {
                    <span class="cell-address">📍 {{ order.or_address }}</span>
                  }
                </td>

                <td class="col-date">
                  <span class="cell-date">{{ formatDate(order.or_order_date) }}</span>
                </td>

                <td class="col-closing">
                  @if (order.or_closing_date && !order.or_closing_date.startsWith('0001-01-01')) {
                    <span class="cell-date cell-date--closed">{{ formatDate(order.or_closing_date) }}</span>
                  } @else {
                    <span class="cell-muted">—</span>
                  }
                </td>

                <td class="col-status">
                  <div class="status-cell">
                    <div class="status-cell__bar">
                      <div
                        class="status-cell__fill"
                        [style.width.%]="progress(order)"
                        [class.status-cell__fill--complete]="progress(order) >= 100"
                      ></div>
                    </div>
                    <div class="status-cell__meta">
                      <span class="status-cell__desc">{{ order.tab_order_status_desc || '—' }}</span>
                      <span class="status-cell__pct">{{ progress(order) }}%</span>
                    </div>
                  </div>
                </td>

                <td class="col-ddt" (click)="$event.stopPropagation()">
                  @if (order.or_url_ddt) {
                    <a
                      class="ddt-link"
                      [attr.href]="order.or_url_ddt"
                      target="_blank"
                      rel="noopener"
                      [title]="order.or_ddt_number || 'Apri DDT'"
                    >📄 PDF</a>
                  } @else {
                    <span class="cell-muted">—</span>
                  }
                </td>

                <td class="col-actions" (click)="$event.stopPropagation()">
                  <div class="row-actions">
                    <button
                      type="button"
                      class="icon-btn icon-btn--details"
                      title="Dettagli ordine"
                      (click)="openOrderDetails(order)"
                    >📦</button>
                    @if (order.os_tracking_number) {
                      <button
                        type="button"
                        class="icon-btn icon-btn--tracking"
                        title="Tracking spedizione"
                        (click)="openTracking(order)"
                      >📍</button>
                    }
                  </div>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>

      @if (totalPages > 1) {
        <nav class="pagination" aria-label="Paginazione ordini">
          <button
            type="button"
            class="pagination__btn"
            [disabled]="currentPage === 0"
            (click)="prevPage()"
          >‹ Precedente</button>
          <span class="pagination__info">
            Pagina <strong>{{ currentPage + 1 }}</strong> di {{ totalPages }}
            · <span class="pagination__total">{{ filteredOrders.length }} ordini</span>
          </span>
          <button
            type="button"
            class="pagination__btn"
            [disabled]="currentPage >= totalPages - 1"
            (click)="nextPage()"
          >Successiva ›</button>
        </nav>
      }
    }
  </section>

  <!-- ─── Embedded children (preserved as-is, separate restyle) ── -->

  <!-- Order details drawer/modal -->
  @if (showOrderDetails) {
    <div class="orders-drawer-backdrop" (click)="closeOrderDetails()">
      <aside class="orders-drawer" (click)="$event.stopPropagation()">
        <header class="orders-drawer__header">
          <div class="orders-drawer__title-block">
            <h2 class="orders-drawer__title">📦 Dettagli ordine {{ detailsOrderNumber || detailsOrderId }}</h2>
          </div>
          <button type="button" class="modal-close" (click)="closeOrderDetails()" aria-label="Chiudi">×</button>
        </header>
        <div class="orders-drawer__body">
          <app-ordini-agente-dettaggli
            [openOrderDettagli]="true"
            [agent_id]="detailsAgentId"
            [order_id]="detailsOrderId"
            [token_agent]="detailsTokenAgent"
            [or_assigned_date]="detailsAssignedDate"
            [status_cod]="detailsStatusCod"
          ></app-ordini-agente-dettaggli>
        </div>
      </aside>
    </div>
  }

  <!-- Tracking widget -->
  @if (showTracking) {
    <div class="orders-drawer-backdrop" (click)="closeTracking()">
      <aside class="orders-drawer orders-drawer--tracking" (click)="$event.stopPropagation()">
        <header class="orders-drawer__header">
          <div class="orders-drawer__title-block">
            <h2 class="orders-drawer__title">📍 Tracking spedizione</h2>
          </div>
          <button type="button" class="modal-close" (click)="closeTracking()" aria-label="Chiudi">×</button>
        </header>
        <div class="orders-drawer__body">
          <app-accesso-funzionari-tracking
            [track_ID]="trackingTrackId"
            [token]="agent_token"
            [agentID]="trackingAgentId"
            [newTrack]="true"
          ></app-accesso-funzionari-tracking>
        </div>
      </aside>
    </div>
  }

</div>
